import type { Asset, AssetKind } from "../types";
import { uid } from "./defaults";
import { deleteLocalAssetFallback, saveLocalAssetFallback } from "./local-asset-fallback";
import { readOpfsFile } from "./workspace-client";
import { sha256Hex } from "./browser-crypto";
import { prepareMapRaster } from "./map-raster";

let assetDirectoryPromise: Promise<FileSystemDirectoryHandle> | undefined;
// Compatibility contract: saved campaigns already point at this OPFS directory.
const LEGACY_ASSET_DIRECTORY = "jftt-assets";

function assetDirectory() {
  assetDirectoryPromise ??= navigator.storage.getDirectory().then((root) => root.getDirectoryHandle(LEGACY_ASSET_DIRECTORY, { create: true }));
  return assetDirectoryPromise;
}

export async function storageStatus() {
  const estimate = await navigator.storage?.estimate?.() ?? {};
  const persisted = await navigator.storage?.persisted?.() ?? false;
  return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0, persisted };
}

export type PersistentStorageRequestResult = "granted" | "denied" | "unsupported";

export async function requestPersistentStorage() {
  if (!navigator.storage || typeof navigator.storage.persist !== "function") return "unsupported" as const;
  return await navigator.storage.persist() ? "granted" as const : "denied" as const;
}

export async function saveAsset(campaignId: string, source: Blob, name: string, kind: AssetKind): Promise<Asset> {
  const bytes = await source.arrayBuffer();
  const hash = await sha256Hex(bytes);
  const extension = name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
  const opfsPath = `${LEGACY_ASSET_DIRECTORY}/${hash}.${extension}`;
  let writtenToOpfs = false; let opfsError: unknown;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const directory = await assetDirectory();
      const handle = await directory.getFileHandle(`${hash}.${extension}`, { create: true });
      const writable = await handle.createWritable();
      try { await writable.write(source); } finally { await writable.close(); }
      writtenToOpfs = true;
      break;
    } catch (error) {
      opfsError = error;
      assetDirectoryPromise = undefined;
      if (attempt === 2) break;
      await new Promise((resolve) => window.setTimeout(resolve, 60 * (attempt + 1)));
    }
  }
  if (!writtenToOpfs) {
    try { await saveLocalAssetFallback(opfsPath, source); }
    catch (fallbackError) {
      const detail = opfsError instanceof Error ? opfsError.message : fallbackError instanceof Error ? fallbackError.message : "Unknown storage error.";
      throw new Error(`Could not write the media file to local storage: ${detail}`);
    }
  }
  const sanitizedName = [...name]
    .map((character) => character.charCodeAt(0) <= 0x1f || '<>:"/\\|?*'.includes(character) ? "_" : character)
    .join("")
    .slice(0, 180);
  return {
    id: uid("asset"), campaignId, name: sanitizedName,
    kind, mime: source.type || "application/octet-stream", size: source.size, hash, opfsPath, createdAt: Date.now(),
  };
}

/** Restore a backup file at the exact path referenced by its asset metadata. */
export async function writeAssetFile(path: string, source: Blob) {
  const parts = path.split("/").filter(Boolean);
  if (!parts.length || parts.some((part) => part === "." || part === ".." || part.includes("\\"))) throw new Error("The backup contains an unsafe asset path.");
  try {
    const root = await navigator.storage.getDirectory();
    let directory = root;
    for (const part of parts.slice(0, -1)) directory = await directory.getDirectoryHandle(part, { create: true });
    const handle = await directory.getFileHandle(parts.at(-1)!, { create: true });
    const writable = await handle.createWritable();
    try { await writable.write(source); } finally { await writable.close(); }
  } catch (opfsError) {
    try { await saveLocalAssetFallback(path, source); }
    catch (fallbackError) {
      const detail = opfsError instanceof Error ? opfsError.message : fallbackError instanceof Error ? fallbackError.message : "Unknown storage error.";
      throw new Error(`Could not restore ${parts.at(-1)} to local storage: ${detail}`);
    }
  }
}

const mimeFromPath = (path: string) => {
  const extension = path.split(".").pop()?.toLowerCase();
  return ({
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif", avif: "image/avif",
    mp4: "video/mp4", m4v: "video/mp4", webm: "video/webm", ogv: "video/ogg",
    mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg", m4a: "audio/mp4", aac: "audio/aac", flac: "audio/flac", opus: "audio/ogg", aiff: "audio/aiff",
  } as Record<string, string>)[extension ?? ""];
};

async function mimeFromBytes(file: Blob) {
  const bytes = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  const has = (...expected: number[]) => expected.every((value, index) => bytes[index] === value);
  if (has(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  if (has(0xff, 0xd8, 0xff)) return "image/jpeg";
  if (has(0x47, 0x49, 0x46, 0x38)) return "image/gif";
  if (has(0x52, 0x49, 0x46, 0x46) && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return "image/webp";
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) return "video/mp4";
  return undefined;
}

/**
 * OPFS files do not reliably retain the browser MIME type. Older imports can
 * also have application/octet-stream metadata, even when their local filename
 * clearly identifies an image. Always recreate a typed Blob before passing
 * it to an image, audio, or video element so existing media still decodes
 * after a refresh.
 */
export async function assetUrl(path: string, mime?: string, options?: { mapPreview?: boolean; width?: number; height?: number }): Promise<string | null> {
  const file = await readOpfsFile(path);
  if (!file) return null;
  const detectedMime = await mimeFromBytes(file);
  const declaredMime = mime && mime !== "application/octet-stream" ? mime : undefined;
  const resolvedMime = detectedMime ?? declaredMime ?? mimeFromPath(path) ?? file.type ?? "application/octet-stream";
  const typed = new Blob([file], { type: resolvedMime });
  // Older campaigns may still contain an original 100+ megapixel map. Render
  // a temporary derivative before assigning it to an <img>, rather than first
  // uploading the huge source into the browser compositor.
  let renderable = typed;
  if (options?.mapPreview && resolvedMime.startsWith("image/")) {
    try { renderable = await prepareMapRaster(typed, options); } catch { /* Fall back to the stored source if a browser cannot resize it. */ }
  }
  return URL.createObjectURL(renderable);
}

export async function deleteAssetFile(path: string) {
  try {
    const root = await navigator.storage.getDirectory();
    const [directoryName, fileName] = path.split("/");
    const directory = await root.getDirectoryHandle(directoryName);
    await directory.removeEntry(fileName);
  } catch { /* already absent */ }
  await deleteLocalAssetFallback(path);
}
